import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  CopyPlusIcon,
  DownloadIcon,
  EyeIcon,
  LibraryBigIcon,
  LinkIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PencilRulerIcon,
  PinIcon,
  TrashIcon,
} from 'lucide-react';

import type { WorkflowView } from '@/lib/api/generated/model';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePreferencesStore } from '@/shared/store/preferences';
import { useCopyWorkflowLink } from '../hooks/useCopyWorkflowLink';
import { isPinned } from '../pinnedWorkflows';
import { CopyToWorkspaceDialog } from './CopyToWorkspaceDialog';
import { DeleteWorkflowDialog } from './DeleteWorkflowDialog';
import { ExportWorkflowDialog } from './ExportWorkflowDialog';
import { ShareToLibraryDialog } from './ShareToLibraryDialog';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * A workflow list row's actions menu.
 *
 * Delete never deletes directly; it opens the confirmation dialog this
 * component also owns, so each row's dialog instance is independent of every
 * other row's.
 *
 * "Open in builder" is the list's route into editing. The builder had no entry
 * point at all until this — the route existed and was reachable only by typing
 * its address, which is not a feature anybody would find.
 */
export function WorkflowRowMenu({
  workflow,
  workspaceId,
  onRename,
}: {
  workflow: WorkflowView;
  workspaceId: string;
  /** Starts the in-place rename the row's name control renders. */
  onRename: () => void;
}) {
  const canWork = useCanWork();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const copyLink = useCopyWorkflowLink(workspaceId, workflow.key);
  const navigate = useNavigate();
  const pinnedIds = usePreferencesStore((state) => state.pinnedWorkflowIds);
  const togglePinnedWorkflow = usePreferencesStore(
    (state) => state.togglePinnedWorkflow,
  );
  const pinned = isPinned(workflow.id, pinnedIds);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Actions for ${workflow.name}`}
              // On a phone the card's link covers the card; the menu sits above
              // it, level with the name (spec 273).
              className="max-md:relative max-md:z-10 max-md:-mt-2.5"
            />
          }
        >
          <MoreHorizontalIcon aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          {/*
            Three groups (spec 210, the review's P7): actions on the workflow
            in this workspace, then the ways to take it elsewhere, then Delete
            on its own at the bottom, away from Share.
          */}
          <DropdownMenuGroup>
            <DropdownMenuItem
              onClick={() =>
                void navigate({
                  to: '/workspaces/$workspaceId/workflows/$workflowKey/builder',
                  params: { workspaceId, workflowKey: workflow.key },
                })
              }
            >
              {/* The same route is the builder's read-only view for someone
                  only reading the workspace (spec 193 FR-008). */}
              {canWork ? (
                <PencilRulerIcon aria-hidden="true" />
              ) : (
                <EyeIcon aria-hidden="true" />
              )}
              {canWork ? 'Open in builder' : 'View in builder'}
            </DropdownMenuItem>
            {/* Rename lives here rather than as a pencil beside the name: one
                row, one menu, one place to look for what can be done to it. */}
            {canWork ? (
              <DropdownMenuItem onClick={onRename}>
                <PencilIcon aria-hidden="true" />
                Rename
              </DropdownMenuItem>
            ) : null}
            {/*
              Pinning lives in this menu rather than behind a hover-revealed
              icon on the sidebar row: a control that only appears on hover is
              unreachable by touch and by keyboard.
            */}
            <DropdownMenuItem
              onClick={() => {
                togglePinnedWorkflow(workflow.id);
              }}
            >
              <PinIcon aria-hidden="true" />
              {pinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup aria-label="Share">
            {/* Anyone who can read a workflow may take it out (spec 203): the
                read-only view already shows all of it. */}
            <DropdownMenuItem onClick={() => setExportOpen(true)}>
              <DownloadIcon aria-hidden="true" />
              Export…
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setCopyOpen(true)}>
              <CopyPlusIcon aria-hidden="true" />
              Copy to workspace…
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void copyLink()}>
              <LinkIcon aria-hidden="true" />
              Copy link
            </DropdownMenuItem>
            {/* Changes what every workspace's Library shows, so it is the
                members' (spec 206), unlike taking a copy out. */}
            {canWork ? (
              <DropdownMenuItem onClick={() => setShareOpen(true)}>
                <LibraryBigIcon aria-hidden="true" />
                Share to Library…
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuGroup>
          {/* Pinning is this person's own; renaming and deleting are the
              members' (spec 189). */}
          {canWork ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <TrashIcon aria-hidden="true" />
                Delete workflow
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteWorkflowDialog
        workspaceId={workspaceId}
        workflow={workflow}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      />
      <ExportWorkflowDialog
        workspaceId={workspaceId}
        workflow={workflow}
        open={exportOpen}
        onOpenChange={setExportOpen}
      />
      <CopyToWorkspaceDialog
        workspaceId={workspaceId}
        workflow={workflow}
        open={copyOpen}
        onOpenChange={setCopyOpen}
      />
      <ShareToLibraryDialog
        workspaceId={workspaceId}
        workflow={workflow}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
    </>
  );
}
